/**
 * Browser-held poll secrets.
 *
 * A poll made with no account has no session to identify anyone, so three
 * things live in the browser instead. They are kept here rather than inline in
 * each page because two pages read them and a mismatched key is silent: the
 * poll still loads, the person just quietly stops being recognised.
 *
 * NONE of these is authority. Every one of them is checked server-side against
 * a hash on the row; a made-up value identifies nobody.
 */

/** The respondent's own row — what makes a second submission an edit. */
export const editTokenKey = (publicId: string) => `saillab_poll_edit_${publicId}`;

/**
 * The creator's manage secret for a poll with no account.
 *
 * localStorage, not sessionStorage: this is the ONLY way back to organizing a
 * poll they made, and it has to survive closing the tab. It is also why the
 * page tells them to keep the link — a cleared browser loses the poll.
 */
export const manageTokenKey = (publicId: string) => `saillab_poll_manage_${publicId}`;

export const readManageToken = (publicId: string): string | null => {
  try {
    return window.localStorage.getItem(manageTokenKey(publicId));
  } catch {
    return null;
  }
};

/**
 * Forget a manage token — used once a poll has been claimed, where the token has
 * been rotated server-side and the stored copy now identifies nobody.
 */
export const clearManageToken = (publicId: string): void => {
  try {
    window.localStorage.removeItem(manageTokenKey(publicId));
  } catch {
    /* nothing useful to do */
  }
};

export const storeManageToken = (publicId: string, token: string): void => {
  try {
    window.localStorage.setItem(manageTokenKey(publicId), token);
  } catch {
    // Private mode / storage blocked. The token is still in the URL, which is
    // the copy that matters.
  }
};

/**
 * "Move this poll into my account", parked while the person goes and makes one.
 *
 * localStorage rather than sessionStorage because OAuth signup leaves and
 * returns through another origin, and on some browsers that is a different tab.
 * Cleared as soon as the claim succeeds — or when it is refused, so a poll
 * somebody else already claimed cannot retry forever.
 */
const PENDING_CLAIM_KEY = 'saillab_poll_pending_claim';

export interface PendingPollClaim {
  publicId: string;
  adminToken: string;
}

export const setPendingClaim = (claim: PendingPollClaim): void => {
  try {
    window.localStorage.setItem(PENDING_CLAIM_KEY, JSON.stringify(claim));
  } catch {
    /* storage blocked — they can press the button again once signed in */
  }
};

export const readPendingClaim = (): PendingPollClaim | null => {
  try {
    const raw = window.localStorage.getItem(PENDING_CLAIM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.publicId === 'string' && typeof parsed?.adminToken === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
};

export const clearPendingClaim = (): void => {
  try {
    window.localStorage.removeItem(PENDING_CLAIM_KEY);
  } catch {
    /* nothing useful to do */
  }
};

/**
 * Has the "move this into an account" offer already been made for this poll?
 *
 * It is a one-time offer, shown on the visit that created the poll. Without a
 * record of having shown it, a refresh or a back-button return would make it
 * appear again, and an offer that keeps reappearing stops reading as an offer.
 */
const claimOfferKey = (publicId: string) => `saillab_poll_offer_${publicId}`;

export const claimOfferAlreadyShown = (publicId: string): boolean => {
  try {
    return window.localStorage.getItem(claimOfferKey(publicId)) === '1';
  } catch {
    return false;
  }
};

export const markClaimOfferShown = (publicId: string): void => {
  try {
    window.localStorage.setItem(claimOfferKey(publicId), '1');
  } catch {
    /* storage blocked — worst case the offer shows once more */
  }
};
